import { Role } from '@auth/user';
import {
	getSessionRedirectUrl,
	resetSessionRedirectUrl,
	setSessionRedirectUrl
} from '@fuse/core/FuseAuthorization/sessionRedirectUrl';
import { FuseRouteObjectType } from '@fuse/core/FuseLayout/FuseLayout';
import withRouter, { WithRouterProps } from '@fuse/core/withRouter/withRouter';
import { getFuseRouteParamUtil } from '@fuse/hooks/useFuseRouteParameter';
import FuseUtils from '@fuse/utils/FuseUtils';
import React, { Component } from 'react';
import FuseLoading from '../FuseLoading';
import { defaultNavigationConfig } from 'src/configs/navigationConfig';

/**
 * Props for the FuseAuthorization component
 */
export type FuseAuthorizationProps = {
	/** The child components to render when access is granted */
	children: React.ReactNode;
	/** The current location object from the router */
	location: Location;
	/** Array of user roles, null if not authenticated */
	userRoles: Role[] | null;
	/** Optional URL to redirect to after login */
	loginRedirectUrl?: string;
} & WithRouterProps;

/**
 * State interface for the FuseAuthorization component
 */
type State = {
	/** Whether the user has access to the current route */
	accessGranted: boolean;
};

/**
 * Determines if a user is a guest (not authenticated or has no roles)
 * @param role - Array of user roles or null
 * @returns True if the user is a guest, false otherwise
 */
function isUserGuest(role: Role[] | null) {
	return !role || (Array.isArray(role) && role?.length === 0);
}

/**
 * Find navigation item auth requirements by URL path
 * @param pathname - The current pathname
 * @returns Array of required roles or null if no auth required
 */
function getNavigationAuth(pathname: string): Role[] | null {
	const navigation = defaultNavigationConfig;

	/**
	 * Recursively search for a navigation item by URL and return auth from item or its parents
	 * @param items - Navigation items to search
	 * @param path - Current path to match
	 * @param parentAuth - Auth from parent items
	 * @returns Auth array or null
	 */
	function findNavItemAuth(items: typeof navigation, path: string, parentAuth: Role[] | null = null): Role[] | null {
		for (const item of items) {
			// Current item's auth takes precedence, otherwise inherit from parent
			const currentAuth = item.auth || parentAuth;

			if (item.url) {
				// Normalize URL for comparison
				const itemUrl = item.url.startsWith('/') ? item.url : `/${item.url}`;

				// Check for exact match or path starts with this URL
				if (path === itemUrl || path.startsWith(itemUrl + '/')) {
					return currentAuth;
				}
			}

			// Search in children if they exist, passing current auth as parent auth
			if (item.children && item.children.length > 0) {
				const childResult = findNavItemAuth(item.children, path, currentAuth);

				if (childResult !== null) {
					return childResult;
				}
			}
		}

		return null;
	}

	// First try exact match
	let result = findNavItemAuth(navigation, pathname);

	// If no exact match, try progressive parent path matching
	if (result === null) {
		const pathSegments = pathname.split('/').filter(Boolean);
		for (let i = pathSegments.length; i > 0; i--) {
			const partialPath = '/' + pathSegments.slice(0, i).join('/');
			result = findNavItemAuth(navigation, partialPath);

			if (result !== null) break;
		}
	}

	return result;
}

/**
 * FuseAuthorization is a higher-order component that wraps its child component which handles the authorization logic of the app.
 * It checks the provided Auth property from FuseRouteItemType (auth property) against the current logged-in user role.
 * 
 * The component manages access control by:
 * - Checking user permissions against route requirements
 * - Redirecting unauthorized users to appropriate pages
 * - Handling both guest and authenticated user scenarios
 * - Managing session redirect URLs for post-login navigation
 */
export class FuseAuthorization extends Component<FuseAuthorizationProps, State> {
	/**
	 * Creates an instance of FuseAuthorization
	 * @param props - The component props
	 */
	constructor(props: FuseAuthorizationProps) {
		super(props);

		this.state = {
			accessGranted: true
		};
	}

	/**
	 * Component lifecycle method called after component mounts
	 * Redirects user if access is not granted
	 */
	componentDidMount() {
		const { accessGranted } = this.state;

		if (!accessGranted) {
			this.redirectRoute();
		}
	}

	/**
	 * Determines if component should update based on access granted state change
	 * @param nextProps - The next props
	 * @param nextState - The next state
	 * @returns True if component should update, false otherwise
	 */
	shouldComponentUpdate(nextProps: FuseAuthorizationProps, nextState: State) {
		const { accessGranted } = this.state;

		return nextState.accessGranted !== accessGranted;
	}

	/**
	 * Component lifecycle method called after component updates
	 * Redirects user if access is not granted
	 */
	componentDidUpdate() {
		const { accessGranted } = this.state;

		if (!accessGranted) {
			this.redirectRoute();
		}
	}

	/**
	 * Static lifecycle method that derives state from props
	 * Determines if user has access to the current route based on their roles and route requirements
	 * 
	 * @param props - The component props
	 * @returns New state object with accessGranted boolean
	 */
	static getDerivedStateFromProps(props: FuseAuthorizationProps) {
		const { location, userRoles } = props;
		const { pathname, search, hash } = location;
		const fullPath = `${pathname}${search || ''}${hash || ''}`;

		// A universal/panel login link (?flag= or ?tag=) to /sign-in must be allowed
		// to render even for an already-authenticated user, since JwtSignInForm's own
		// effect is what swaps the session over to the new user. Without this, the
		// guest-only guard below bounces the user away before that effect ever runs,
		// and the flag is silently dropped. Trailing slash is stripped since
		// react-router itself treats '/sign-in' and '/sign-in/' as the same route.
		if (pathname.replace(/\/+$/, '') === '/sign-in') {
			const searchParams = new URLSearchParams(search);

			if (searchParams.get('flag') || searchParams.get('tag')) {
				return { accessGranted: true };
			}
		}

		// Get auth from route configuration
		const routeAuth = getFuseRouteParamUtil<FuseRouteObjectType['auth']>(pathname, 'auth', false) as Role[];

		// Get auth from navigation configuration
		const navAuth = getNavigationAuth(pathname);

		// Use route auth if available, otherwise fall back to navigation auth
		const auth = navAuth || routeAuth;

		const ignoredPaths = ['/', '/callback', '/sign-in', '/logout'];

		// Check if auth is empty array (only guest allowed)
		const isOnlyGuestAllowed = Array.isArray(auth) && auth.length === 0;
		const isGuest = isUserGuest(userRoles);

		const userHasPermission = FuseUtils.hasPermission(auth, userRoles);

		// Set redirect URL if user lacks permission and is not on ignored path
		if (auth && !userHasPermission && !ignoredPaths.includes(pathname)) {
			setSessionRedirectUrl(fullPath);
		}

		/**
		 * If user is member but don't have permission to view the route
		 * redirected to main route '/' or 401 page
		 * Hidden pages are still accessible to authorized users
		 */
		if (!userHasPermission && !isGuest && !ignoredPaths.includes(pathname)) {
			if (isOnlyGuestAllowed) {
				setSessionRedirectUrl('/');
			} else {
				setSessionRedirectUrl('401');
			}
		}

		return {
			accessGranted: auth ? userHasPermission : true
		};
	}

	/**
	 * Redirects the user to appropriate route based on their authentication status
	 * - Guest users are redirected to sign-in page
	 * - Authenticated users are redirected to dashboard or stored redirect URL
	 */
	redirectRoute() {
		const { userRoles, navigate, loginRedirectUrl = '/' } = this.props;
		const redirectUrl = getSessionRedirectUrl() || loginRedirectUrl;

		/*
		 * User is guest
		 * Redirect to Login Page
		 */
		if (isUserGuest(userRoles)) {
			setTimeout(() => navigate('/sign-in'), 0);
		} else {

			/*
			 * User is member
			 * User must be on unauthorized page or just logged in
			 * Redirect to dashboard or loginRedirectUrl
			 */
			setTimeout(() => navigate(redirectUrl), 0);
			resetSessionRedirectUrl();
		}
	}

	/**
	 * Renders the component
	 */
	render() {
		const { accessGranted } = this.state;
		const { children } = this.props;
		return accessGranted ? children : <FuseLoading />;
	}
}

/**
 * FuseAuthorization component wrapped with router HOC
 * Provides routing capabilities to the authorization component
 */
const FuseAuthorizationWIthRouter = withRouter(FuseAuthorization);

export default FuseAuthorizationWIthRouter;
